import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import { CheckIcon, CloseIcon, PlusIcon, TasksIcon } from '../../../template/kartera/icons'
import * as Permisos from '../../../utils/Permisos'
import { PRIORITY_STYLES } from '../common/task-common'
import TaskDetailDrawer from '../detail-drawer/task-detail-drawer'
import TaskFormDrawer from '../form-drawer/task-form-drawer'
import { useTaskDrawerModel } from './task-drawer-model'
import type { TaskDrawerItem, TaskDrawerProps, TaskGroupKey } from './task-drawer-model'

export type { TaskDrawerProps } from './task-drawer-model'

const SKELETON_ROWS = [1, 2, 3, 4]

const GROUP_TONES: Record<TaskGroupKey, string> = {
    vencidas: 'text-k-danger-ink',
    hoy: 'text-tone-orange-ink',
    proximas: 'text-k-muted',
    sinFecha: 'text-k-muted',
    completadas: 'text-k-ok',
}

const DUE_TONES: Record<TaskGroupKey, string> = {
    vencidas: 'font-semibold text-k-danger-ink',
    hoy: 'font-semibold text-tone-orange-ink',
    proximas: 'text-k-ink-2',
    sinFecha: 'text-k-muted',
    completadas: 'text-k-ok',
}

const TaskItem = ({ item, canWrite, onToggle, onOpen }: { item: TaskDrawerItem; canWrite: boolean; onToggle: () => void; onOpen: () => void }) => (
    <li className="flex gap-4 border-t border-k-line px-6 py-4 transition-colors hover:bg-k-hover">
        <button
            type="button"
            role="checkbox"
            aria-checked={item.checked}
            aria-label={item.checked ? `Reabrir ${item.nombre}` : `Completar ${item.nombre}`}
            disabled={!canWrite || item.busy}
            onClick={onToggle}
            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 transition-colors disabled:cursor-not-allowed ${item.checked
                ? 'border-k-ok bg-k-ok text-white'
                : 'border-k-line-strong bg-transparent text-transparent enabled:hover:border-k-primary'} ${item.busy ? 'opacity-60' : ''}`}
        >
            <CheckIcon size={14} strokeWidth={3} />
        </button>
        <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-1 border-0 bg-transparent p-0 text-left">
            <span className={`text-base font-bold ${item.checked ? 'text-k-muted line-through' : 'text-k-ink'}`}>{item.nombre}</span>
            {item.descripcion && <span className="line-clamp-2 text-sm text-k-muted">{item.descripcion}</span>}
            <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px]">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-xs font-bold ${item.prioridad ? PRIORITY_STYLES[item.prioridad] : 'bg-k-line-soft text-k-ink-2'}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {item.prioridadLabel}
                </span>
                <span className="rounded-full bg-k-line-soft px-2.5 py-[3px] text-xs font-bold text-k-ink-2">{item.tipoLabel}</span>
                <span className={DUE_TONES[item.group]}>{item.dueText}</span>
                <span className="text-k-muted">· {item.seller || 'Sin asignar'}</span>
            </span>
        </button>
    </li>
)

const TaskDrawer = (props: TaskDrawerProps) => {
    const { open, onClose } = props
    const model = useTaskDrawerModel(props)

    return (
        <>
            <Drawer open={open} onClose={onClose} bare width={600}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-labelledby="task-drawer-title">
                    <div className="flex shrink-0 items-center gap-3.5 border-b border-k-line px-6 py-5">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-k-accent-soft text-k-accent-ink">
                            <TasksIcon size={22} />
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col">
                            <h2 id="task-drawer-title" className="m-0 text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">Tareas</h2>
                            <p className="m-0 truncate text-sm text-k-muted">{model.subtitle}</p>
                        </div>
                        <GrantContent permisos={[Permisos.TAREAS_W]}>
                            <button
                                type="button"
                                className="inline-flex h-12 shrink-0 items-center gap-2 rounded-xl bg-k-primary px-5 text-[15px] font-bold !text-white hover:brightness-105"
                                onClick={model.openForm}
                            >
                                <PlusIcon />
                                Nueva
                            </button>
                        </GrantContent>
                        <button type="button" aria-label="Cerrar" className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                            <CloseIcon size={18} />
                        </button>
                    </div>

                    <div role="tablist" aria-label="Filtrar tareas" className="flex shrink-0 gap-2 overflow-x-auto px-6 pb-3 pt-4">
                        {model.tabs.map((tab) => {
                            const active = tab.key === model.tab
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    onClick={() => model.selectTab(tab.key)}
                                    className={`inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-4 text-[15px] font-semibold transition-colors ${active
                                        ? 'border-transparent bg-k-ink text-k-surface'
                                        : 'border-k-line-strong bg-transparent text-k-ink hover:bg-k-hover'}`}
                                >
                                    {tab.label}
                                    <span className={`font-medium ${active ? 'opacity-70' : 'text-k-muted'}`}>{tab.count}</span>
                                </button>
                            )
                        })}
                    </div>

                    <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto pb-6 ${model.refreshing ? 'opacity-70' : ''}`}>
                        {model.actionError && (
                            <div className="mx-6 mb-3 flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3 text-sm text-k-danger-ink" role="alert">
                                <ErrorOutlineIcon style={{ fontSize: 20 }} />
                                <p className="m-0 flex-1">{model.actionError}</p>
                                <button type="button" aria-label="Cerrar aviso" className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-inherit hover:bg-k-hover" onClick={model.dismissActionError}>
                                    <CloseIcon size={14} />
                                </button>
                            </div>
                        )}

                        {model.initialLoading && (
                            <ul className="m-0 list-none p-0 pt-2" aria-label="Cargando tareas">
                                {SKELETON_ROWS.map((row) => (
                                    <li key={row} className="flex gap-4 border-t border-k-line px-6 py-4">
                                        <div className="h-7 w-7 shrink-0 animate-pulse rounded-lg bg-k-line" />
                                        <div className="flex flex-1 flex-col gap-2">
                                            <div className="h-4 w-40 animate-pulse rounded bg-k-line" />
                                            <div className="h-3 w-56 animate-pulse rounded bg-k-line" />
                                            <div className="h-5 w-64 animate-pulse rounded-full bg-k-line" />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {model.loadError && !model.initialLoading && (
                            <div className="mx-6 mt-2 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="m-0 text-sm text-k-danger-ink">{model.loadError}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {!model.initialLoading && !model.loadError && model.groups.length === 0 && (
                            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
                                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-k-ok-soft text-k-ok">
                                    <CheckIcon size={26} />
                                </span>
                                <p className="m-0 text-sm text-k-muted">{model.emptyMessage}</p>
                            </div>
                        )}

                        {!model.initialLoading && !model.loadError && model.groups.map((group) => (
                            <section key={group.key} aria-label={group.label} className="pt-2">
                                <h3 className={`m-0 px-6 pb-3 text-[13px] font-extrabold uppercase tracking-[0.08em] ${GROUP_TONES[group.key]}`}>
                                    {group.label} · {group.items.length}
                                </h3>
                                <ul className="m-0 list-none p-0">
                                    {group.items.map((item) => (
                                        <TaskItem
                                            key={item.id}
                                            item={item}
                                            canWrite={model.canWrite}
                                            onToggle={() => model.toggle(item.id)}
                                            onOpen={() => model.openDetail(item.id)}
                                        />
                                    ))}
                                </ul>
                            </section>
                        ))}

                        {model.refreshing && (
                            <div className="flex justify-center py-3 text-k-muted">
                                <RefreshIcon className="animate-spin" style={{ fontSize: 20 }} />
                            </div>
                        )}
                    </div>
                </div>
            </Drawer>

            <TaskFormDrawer open={model.formOpen} onClose={model.closeForm} onCreated={model.created} />
            <TaskDetailDrawer tareaId={model.detailId} onClose={model.closeDetail} onEdit={model.editTarea} onChanged={model.reload} />
        </>
    )
}

export default TaskDrawer
